import { InvoiceItemDto } from '@/types';

/**
 * Shared invoice calculation utilities.
 * These functions are used across invoice modals to ensure consistent calculations.
 */

/**
 * Round a number to 2 decimal places for currency calculations.
 * Avoids floating-point precision issues (e.g., 63.30 - 60.0 = 3.299999999999997).
 * 
 * @param value - The number to round
 * @returns The number rounded to 2 decimal places
 */
export function roundCurrency(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * What the customer is asked to pay now. With a deposit it's the part of the deposit not yet paid,
 * capped at the balance: clamp(deposit - paid, 0, balance). Without one it's the whole balance.
 * Same rule as DepositDue in DispatchInvoice's PdfGenerationService.
 */
export function amountDueNow(total: number, amountPaid: number, deposit?: number | null): number {
    const balance = roundCurrency(total - amountPaid);
    if (deposit == null) return balance;
    return roundCurrency(Math.max(Math.min(deposit - amountPaid, balance), 0));
}

/**
 * Calculate subtotal: sum of (price × quantity) for all items
 */
export function calculateSubtotal(items: InvoiceItemDto[]): number {
    return (items || []).reduce((sum, item) => sum + item.price * item.quantity, 0);
}

/**
 * Calculate total discount: sum of discount amounts for all items.
 * Amount type: discount × quantity (per-unit)
 * Percent type: price × quantity × discount / 100
 */
export function calculateDiscount(items: InvoiceItemDto[]): number {
    return (items || []).reduce((sum, item) => {
        const discountAmount = item.discount_type === 'Percent'
            ? item.price * item.quantity * item.discount / 100
            : item.discount * item.quantity;
        return sum + discountAmount;
    }, 0);
}

/**
 * Calculate tax amount based on taxable items and tax rate.
 * Only items where taxable !== false are included in the taxable amount.
 * 
 * @param items - Invoice items
 * @param taxRate - Tax rate as a percentage (e.g., 7 for 7%)
 * @returns Tax amount
 */
export function calculateTax(items: InvoiceItemDto[], taxRate: number): number {
    if (!taxRate) return 0;

    // Only tax items explicitly marked taxable (default true)
    const taxableAmount = (items || [])
        .filter((item) => item.taxable !== false)
        .reduce((sum, item) => {
            const discountAmount = item.discount_type === 'Percent'
                ? item.price * item.quantity * item.discount / 100
                : item.discount * item.quantity;
            return sum + (item.price * item.quantity - discountAmount);
        }, 0);

    return roundCurrency(taxableAmount * (taxRate / 100));
}

/**
 * Calculate grand total: subtotal - discount + tax
 * 
 * @param items - Invoice items
 * @param taxRate - Tax rate as a percentage (e.g., 7 for 7%)
 * @returns Grand total
 */
export function calculateTotal(items: InvoiceItemDto[], taxRate: number): number {
    const subtotal = calculateSubtotal(items);
    const discount = calculateDiscount(items);
    const tax = calculateTax(items, taxRate);
    return roundCurrency(subtotal - discount + tax);
}
